import {
  shippingCardOverAtom,
  selectedCarListAtom,
  shippingListAtom,
  timeLineAtom,
  changeCarListAtom,
  timeLineChgArrAtom,
  backdropAtom,
  targetShippingSeqAtom,
} from '@/commons/constants';
import { Button, Card, Checkbox, Collapse, Divider, Switch, Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RouteIcon from '@mui/icons-material/Route';
import ReplayIcon from '@mui/icons-material/Replay';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import styled from 'styled-components';
import { Car, ShippingDetail, TimeLine } from '@/types/result';
import { distanceStringMeterToKm, secondsToTimestamp } from '@/commons/utils';
import MinTimeLine from './MinTimeline';
import * as _ from 'lodash';
import { memo, useCallback } from 'react';
import { useStore } from 'jotai';

import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DepartureBoardIcon from '@mui/icons-material/DepartureBoard';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import { planShippingListAtom, postManualShipping, shippingTargetDetail } from '@/interfaces/plan/shipping';

const MainWrap = styled.div`
  flex-grow:1;
  width: 100%;
  min-width: 260px;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ListWrap = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    flex-grow:1;
    overflow-y: auto;
    > div: first-child {
      display: flex;
      flex-direction: column;
    }
`;

export default function ShippingList() {
  const setBackdrop = useSetAtom(backdropAtom);
  const { refetch } = useAtomValue(shippingTargetDetail);
  const { refetch: listRefetch } = useAtomValue(planShippingListAtom);
  const setTargetShippingSeq = useSetAtom(targetShippingSeqAtom);

  const shippingList = useAtomValue(shippingListAtom);

  const [selectedCarList, setSelectedCarList] = useAtom(selectedCarListAtom);

  const [timeLine, setTimeline] = useAtom(timeLineAtom);
  const [changeCarList, setChangeCarList] = useAtom(changeCarListAtom);

  const { data } = useAtomValue(shippingTargetDetail);

  const copyArray = useAtomValue(timeLineChgArrAtom);

  const handeAllClick = () => {
    if (shippingList.length === selectedCarList.length) {
      setSelectedCarList([]);
    } else {
      setSelectedCarList(shippingList.map((item: ShippingDetail) => item.car.car_seq));
    }
  };

  const handleReRouteClick = async () => {
    if (changeCarList && changeCarList.length > 0) {
      setBackdrop(true);
      const shippingList = _.map(changeCarList, (v) => {
        const target = _.find(copyArray, ['car.car_seq', v]);
        const destIdList = _.map(target.destList, (dest) => {
          return { id: dest.id };
        });
        return {
          car: { car_seq: v },
          destList: destIdList,
        };
      });
      const ret = await postManualShipping({
        deliverDate: data.shipping.date,
        r_shipping_seq: data.shipping.seq,
        t_shipping_seq: data.shipping.t_shipping_seq,
        option: data.shipping.option.shipping_type,
        shippingList: shippingList,
      });
      setBackdrop(false);
      if (ret && ret.data.data.t_shipping_seq) {
        listRefetch();
        setChangeCarList([]);
        setTargetShippingSeq(ret.data.data.t_shipping_seq);
      }
    }
  };

  const handleResetClick = () => {
    setBackdrop(true);
    setChangeCarList([]);
    setSelectedCarList(shippingList.map((item: ShippingDetail) => item.car.car_seq));
    refetch();
  };

  return (
    <MainWrap>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="contained" onClick={handeAllClick} startIcon={<DoneAllIcon />}>
          전체선택
        </Button>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
          <Switch
            color="warning"
            checked={timeLine}
            disabled={shippingList?.length < 1}
            onChange={() => setTimeline(!timeLine)}
          />
          {timeLine === true && (
            <>
              <Button
                variant="contained"
                disabled={changeCarList.length === 0}
                color="warning"
                onClick={handleReRouteClick}
                startIcon={<PublishedWithChangesIcon />}
              >
                경로 재탐색
              </Button>
              <Button
                variant="contained"
                disabled={changeCarList.length === 0}
                color="error"
                onClick={handleResetClick}
                startIcon={<ReplayIcon />}
              >
                초기화
              </Button>
            </>
          )}
        </div>
      </div>
      <Divider />
      <ListWrap>
        <div>
          <div style={{ width: '100%', height: '1.5rem', minHeight: '1.5rem', borderBottom: '1.5px solid #444499' }} />
          {copyArray &&
            copyArray.length > 0 &&
            copyArray.map((item: TimeLine) => {
              return (
                <CarCard
                  key={item.car.car_seq}
                  car={item.car}
                  color={item.color}
                  deliveryCount={item.destLength}
                  orderCount={item.orderLength}
                  distance={distanceStringMeterToKm(item.distance)}
                  time={secondsToTimestamp(item.time)}
                  isSelected={selectedCarList.includes(item.car.car_seq)}
                />
              );
            })}
        </div>
        <Collapse in={timeLine} orientation="horizontal">
          <div style={{ width: '400px' }}>
            <MinTimeLine />
          </div>
        </Collapse>
      </ListWrap>
    </MainWrap>
  );
}

interface CarCardProp {
  car: Car;
  color: string;
  deliveryCount: number;
  orderCount: number;
  distance: string;
  time: string;
  isSelected: boolean;
}

const CardWrap = styled(Card)<{ color: string }>`
  border-radius: 0px;
  border-bottom: 1.5px solid #444499;
  min-height: 3rem;
  display:flex;
  justify-content: space-between;
  align-items: center;
  width: 260px;
  .row {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    > div > h6 {
      padding: 0;
      margin: 0;
      // color: ${({ color }) => color};
    }
  }
`;

const CarCard = memo(({ car, color, deliveryCount, orderCount, distance, time, isSelected }: CarCardProp) => {
  const store = useStore();
  const setSelectedCarList = useSetAtom(selectedCarListAtom);
  const setShippingCardOver = useSetAtom(shippingCardOverAtom);

  const handleCheckboxClick = useCallback((e: any, carId: number) => {
    const current = store.get(selectedCarListAtom);
    if (e.target.checked) {
      setSelectedCarList([...current, carId]);
    } else {
      setSelectedCarList(current.filter((item: number) => item !== carId));
    }
  }, [store, setSelectedCarList]);

  return (
    <CardWrap
      color={color}
      onMouseOver={() => setShippingCardOver(car.car_seq)}
      onMouseOut={() => setShippingCardOver(undefined)}
    >
      <div style={{ width: '100%' }}>
        <div className="row">
          <Checkbox
            sx={{ color, '&.Mui-checked': { color }, padding: 0, margin: 0 }}
            size="medium"
            checked={isSelected}
            onChange={(e) => handleCheckboxClick(e, car.car_seq)}
          />
          <LocalShippingIcon htmlColor={color} />
          <Typography variant="h6">{car.name}</Typography>
        </div>
        <div
          style={{
            width: '100%',
            paddingLeft: '0.5rem',
            display: 'grid',
            gridTemplateColumns: '2.5rem 2.5rem 3rem auto ',
            gap: '0.7rem',
            // justifyContent: 'space-around',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '0.1rem' }}>
            <EmojiTransportationIcon htmlColor="#777" />
            <Typography component="span" variant="body1">
              {deliveryCount}
            </Typography>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '0.1rem' }}>
            <ListAltIcon htmlColor="#777" />
            <Typography component="span" variant="body1">
              {orderCount}
            </Typography>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '0.1rem' }}>
            <DepartureBoardIcon htmlColor="#777" />
            <Typography component="span" variant="body1">
              {time}
            </Typography>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '0.1rem' }}>
            <RouteIcon htmlColor="#777" />
            <Typography component="span" variant="body1">
              {distance}
            </Typography>
          </div>
        </div>
      </div>
    </CardWrap>
  );
});
